import React from 'react';
import { Typography } from '@guildeducationinc/recess';
import { ProgramContentItems } from 'src/components/ProgramDetails/ProgramContentItems';
import { RequirementSet } from 'src/components/ProgramDetails/Requirements/EducationRequirements/RequirementSet';
import { GetMemberProgramQuery, ShortProgramKind } from 'src/graphqlTypes/catalogServiceTypes';
import { useSearchContext } from '@src/components/Providers/SearchProvider';
import { employerIds } from '@src/constants/employerResources';
import { WalmartEducationRequirements } from './WalmartEducationRequirements/WalmartEducationRequirements';
import styles from './EducationRequirements.module.scss';

interface Props {
	programId: string;
	requirements: GetMemberProgramQuery['memberProgram']['requirements'];
	shortProgramKind?: GetMemberProgramQuery['memberProgram']['shortProgramKind'];
}

export const formatGpa = (gpa: number): string | number => {
	if (Number.isInteger(gpa)) {
		return gpa.toFixed(1);
	}
	return gpa;
};

export const EducationRequirements: React.FC<Props> = ({
	programId,
	requirements,
	shortProgramKind,
}) => {
	const {
		minEduLevelDetails,
		minHsGpa,
		minHsGpaDetails,
		minCollegeGpa,
		minCollegeGpaDetails,
		minCollegeCredits,
		minCollegeCreditsDetails,
	} = requirements;
	const { employerUuid } = useSearchContext();
	const isWalmart = employerUuid === employerIds.walmart;

	const hsRequirementSet: GetMemberProgramQuery['memberProgram']['contentItems'] = [];
	const collegeRequirementSet: GetMemberProgramQuery['memberProgram']['contentItems'] = [];
	const educationRequirements: GetMemberProgramQuery['memberProgram']['contentItems'] = [];

	if (minEduLevelDetails) {
		const eduLevel = { body: minEduLevelDetails };
		hsRequirementSet.push(eduLevel);
		educationRequirements.push(eduLevel);
	}

	if (minHsGpa) {
		const hsGpa = {
			title: `${formatGpa(minHsGpa)} minimum high school GPA required`,
			body: minHsGpaDetails,
		};
		hsRequirementSet.push(hsGpa);
		educationRequirements.push(hsGpa);
	}

	if (minCollegeCredits) {
		const collegeCredits = {
			title: `${minCollegeCredits} minimum college credits required`,
			body: minCollegeCreditsDetails,
		};
		collegeRequirementSet.push(collegeCredits);
		educationRequirements.push(collegeCredits);
	}

	if (minCollegeGpa) {
		const collegeGpa = {
			title: `${formatGpa(minCollegeGpa)} minimum college GPA required`,
			body: minCollegeGpaDetails,
		};
		collegeRequirementSet.push(collegeGpa);
		educationRequirements.push(collegeGpa);
	}

	if (educationRequirements.length === 0) {
		return null;
	}

	// custom Walmart requirements, PATH-743
	if (isWalmart && shortProgramKind === ShortProgramKind.BACHELORS) {
		return (
			<WalmartEducationRequirements
				contentItems={educationRequirements}
				programId={programId}
				requirements={requirements}
			/>
		);
	}

	if ((minHsGpa && minCollegeGpa) || (minHsGpa && minCollegeCredits)) {
		return (
			<>
				<section aria-label='education' className={styles.container}>
					<Typography variant='title3'>Education</Typography>
					<Typography
						className={styles.subtitle}
						data-testid='conditional-edu-requirements'
						variant='body2'
					>
						You must meet at least one of the following sets of education requirements:
					</Typography>
					<RequirementSet contentItems={hsRequirementSet} level='High school' />
					<Typography className={styles.or} variant='label2'>
						Or
					</Typography>
					<RequirementSet contentItems={collegeRequirementSet} level='College' />
				</section>
			</>
		);
	}

	return (
		<ProgramContentItems
			ariaLabel='education requirements'
			contentItems={educationRequirements}
			sectionTitle='Education'
		/>
	);
};
